import React, { useEffect, useState } from 'react';
import { SxProps } from '@mui/material';
import { Person } from '@mui/icons-material';
import { useAppSelector } from '@/hooks/redux';
import { useQuery, useQueryClient } from 'react-query';
import api from '@/services/api';
import { getPlayerPhotoUrl, hasPlayerPhoto } from '@/utils/photoUtils';
import { PreloadedAvatar } from '@/components/common/PreloadedAvatar';

interface PlayerPhotoData {
  profilePhotoType?: 'UPLOAD' | 'GRAVATAR' | string;
  profilePhotoUrl?: string | null;
  email?: string | null;
}

interface PlayerAvatarProps {
  playerId: string;
  size?: 'small' | 'medium' | 'large';
  sx?: SxProps;
  /** Optional player data for direct URL generation (skips API calls) */
  player?: PlayerPhotoData;
  /** Background color for the fallback icon (defaults to primary.main) */
  fallbackBgColor?: string;
}

const sizeMap = {
  small: { width: 40, height: 40 },
  medium: { width: 80, height: 80 },
  large: { width: 120, height: 120 }
};

async function fetchPlayerPhotoUrl(
  playerId: string,
  size: string,
  token: string
): Promise<string | null> {
  // First check if player has a photo configured
  const configResponse = await api.get(
    `/players/${playerId}/profile-photo/config`
  );

  const config = configResponse.data;

  // Only fetch image URL if player has an uploaded photo or Gravatar email
  if (
    (config.type === 'UPLOAD' && config.hasUploadedPhoto) ||
    (config.type === 'GRAVATAR' && config.email)
  ) {
    const urlResponse = await api.get(
      `/players/${playerId}/profile-photo/url/${size}`
    );

    if (urlResponse.data.url) {
      return `${urlResponse.data.url}?token=${encodeURIComponent(token)}`;
    }
  }

  return null;
}

export const PlayerAvatar: React.FC<PlayerAvatarProps> = React.memo(({
  playerId,
  size = 'medium',
  sx,
  player,
  fallbackBgColor
}) => {
  const { token } = useAppSelector((state) => state.auth);
  const queryClient = useQueryClient();
  const [refreshKey, setRefreshKey] = useState(0);

  // Calculate URL directly from player data if available
  // Returns: string (has photo), null (no photo), undefined (no player data)
  const directUrl = React.useMemo(() => {
    if (!player) return undefined; // No player data, need to fetch
    if (!token) return undefined;
    if (hasPlayerPhoto(player)) {
      return getPlayerPhotoUrl(player, size, token);
    }
    return null; // Player provided but has no photo
  }, [player, size, token]);

  // Only use API query if player data is not provided
  const shouldFetchFromApi = !player && !!playerId && !!token && playerId !== 'wizard-temp';

  const { data: apiImageUrl, isLoading: isApiLoading } = useQuery(
    ['player-avatar', playerId, size, refreshKey],
    () => fetchPlayerPhotoUrl(playerId, size, token!),
    {
      enabled: shouldFetchFromApi,
      staleTime: 10 * 60 * 1000, // 10 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      retry: false, // Don't retry on failure to avoid hammering API
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  // Determine final image URL
  // undefined = still loading, null = no photo, string = has photo
  const imageUrl = React.useMemo(() => {
    // Auth not ready yet — treat as loading, not "no photo"
    if (!token) return undefined;
    // If we have player data, use direct calculation
    if (directUrl !== undefined) return directUrl;
    // If API is still loading, return undefined
    if (shouldFetchFromApi && isApiLoading) return undefined;
    // Use API result (null or URL)
    return apiImageUrl ?? null;
  }, [token, directUrl, shouldFetchFromApi, isApiLoading, apiImageUrl]);

  useEffect(() => {
    const handlePhotoUpdate = () => {
      // Invalidate cache and refetch
      queryClient.invalidateQueries(['player-avatar', playerId]);
      setRefreshKey((k) => k + 1);
    };

    window.addEventListener('playerPhotoUpdated', handlePhotoUpdate);
    return () => {
      window.removeEventListener('playerPhotoUpdated', handlePhotoUpdate);
    };
  }, [playerId, queryClient]);

  const dimensions = sizeMap[size];

  return (
    <PreloadedAvatar
      src={imageUrl}
      size={dimensions.width}
      fallbackIcon={<Person sx={{ fontSize: dimensions.width * 0.6, color: 'white' }} />}
      fallbackBgColor={fallbackBgColor}
      sx={sx}
    />
  );
});
